import { useMutation, useQueryClient } from "@tanstack/react-query";
import { deleteInventory, getInventoryItem } from "../../api/inventory";
import { Fragment } from "react";
import { useSetRecoilState } from "recoil";
import {
  InOutModalTitleState,
  inOutResetFieldsState,
  incomingItemsModalTitleState,
  incomingItemsResetFieldsState,
  invResetFieldsState,
  inventoryModalTitleState,
  toastBgState,
  toastMessageState,
  toastShowState,
} from "../../utils/atoms/atoms";
import { deleteInOut, getInOutEntry } from "../../api/inout";
import { closeModal } from "../../utils/funcs/common";
import { HasRole } from "../../context/HasRole";
import {
  deleteChallan,
  deleteSentRecd,
  getSentRecdEntry,
} from "../../api/incoming";

interface IInventoryAddDeleteButton {
  part_id: any;
  btnType: "edit" | "delete";
  featureType?: string;
}
const InventoryAddDeleteButton = ({
  part_id,
  btnType,
  featureType = "inventory",
}: IInventoryAddDeleteButton) => {
  const queryClient = useQueryClient();

  const setIncomingItemsModalTitle = useSetRecoilState(
    incomingItemsModalTitleState
  );
  const setInventoryModalTitle = useSetRecoilState(inventoryModalTitleState);
  const setInOutFormModalTitle = useSetRecoilState(InOutModalTitleState);
  const setIncomingItemsResetFields = useSetRecoilState(
    incomingItemsResetFieldsState
  );
  const setInvResetFields = useSetRecoilState(invResetFieldsState);
  const setInOutResetFields = useSetRecoilState(inOutResetFieldsState);
  const setToastShow = useSetRecoilState(toastShowState);
  const setToastBg = useSetRecoilState(toastBgState);
  const setToastMessage = useSetRecoilState(toastMessageState);

  const isEdit = btnType === "edit";

  const inventoryItemMutation = useMutation({
    mutationFn: async (id: number) => {
      return getInventoryItem(id);
    },
    onSuccess(data) {
      queryClient.setQueryData(["inventory-item"], data);
      queryClient.invalidateQueries(["inventory-item"]);
      setInventoryModalTitle("Edit Inventory");
      setInvResetFields(true);
    },
    onError() {
      setToastShow(true);
      setToastBg("danger");
      setToastMessage("Something went wrong");
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (id: number) => {
      return deleteInventory(id);
    },
    onSuccess() {
      queryClient.invalidateQueries(["inventory"]);
      setToastShow(true);
      setToastBg("success");
      setToastMessage("Entry deleted successfully");
    },
    onError(error: any) {
      setToastShow(true);
      setToastBg("danger");
      setToastMessage(error.message || "Something went wrong");
    },
  });

  const editInOutMutation = useMutation({
    mutationFn: async (id: number) => {
      return getInOutEntry(id);
    },
    onSuccess(data) {
      queryClient.setQueryData(["in-out-item"], data);
      setInOutFormModalTitle("Edit Entry");
      closeModal();
      setInOutResetFields(true);
    },
  });

  const deleteInOutMutation = useMutation({
    mutationFn: async (id: number) => {
      return deleteInOut(id);
    },
    onSuccess() {
      queryClient.invalidateQueries(["stock-data"]);
      setToastShow(true);
      setToastBg("success");
      setToastMessage("Entry deleted successfully");
    },
    onError() {
      setToastShow(true);
      setToastBg("danger");
      setToastMessage("Something went wrong");
    },
  });

  const editIncomingMutation = useMutation({
    mutationFn: async (id: number) => {
      return getSentRecdEntry(id);
    },
    onSuccess(data) {
      queryClient.setQueryData(["incoming-item"], data);
      setIncomingItemsModalTitle("Edit Incoming Entry");
      setIncomingItemsResetFields(true);
    },
  });

  const deleteIncomingtMutation = useMutation({
    mutationFn: async (id: number) => {
      return deleteSentRecd(id);
    },
    onSuccess() {
      queryClient.invalidateQueries(["incoming-sent"]);
      queryClient.invalidateQueries(["incoming-recd"]);
      queryClient.invalidateQueries(["incoming-pending"]);
      setToastShow(true);
      setToastBg("success");
      setToastMessage("Entry deleted successfully");
    },
    onError(error: any) {
      setToastShow(true);
      setToastBg("danger");
      setToastMessage(error.message || "Something went wrong");
    },
  });

  const deleteIncomingChallantMutation = useMutation({
    mutationFn: async (id: number) => {
      return deleteChallan(id);
    },
    onSuccess() {
      queryClient.invalidateQueries(["incoming-challan"]);
      setToastShow(true);
      setToastBg("success");
      setToastMessage("Entry deleted successfully");
    },
    onError() {
      setToastShow(true);
      setToastBg("danger");
      setToastMessage("Something went wrong");
    },
  });

  let modalId = "#exampleModal";
  let roles = ["admin", "accounts", "production"];
  if (featureType === "inout") {
    modalId = "#inOutModal";
  }
  if (featureType === "incoming") {
    modalId = "#incomingItemModal";
    roles = ["admin", "accounts"];
  }
  if (featureType === "challan-incoming") {
    modalId = "#incomingChallanItemModal";
    roles = ["admin"];
  }
  return (
    <Fragment>
      <HasRole roles={roles}>
        <div
          className={`styled-btn--dark inv-${btnType}`}
          data-bs-toggle={isEdit ? "modal" : ""}
          data-bs-target={isEdit ? modalId : ""}
          onClick={() => {
            if (isEdit) {
              if (featureType === "inventory") {
                inventoryItemMutation.mutate(part_id);
              } else if (featureType === "inout") {
                editInOutMutation.mutate(part_id);
              } else {
                editIncomingMutation.mutate(part_id);
              }
            } else {
              if (confirm("are you sure?")) {
                if (featureType === "inventory") {
                  deleteItemMutation.mutate(part_id);
                } else if (featureType === "inout") {
                  deleteInOutMutation.mutate(part_id);
                } else if (featureType === "challan-incoming") {
                  deleteIncomingChallantMutation.mutate(part_id);
                } else {
                  deleteIncomingtMutation.mutate(part_id);
                }
              }
            }
          }}
        >
          <div>
            <i
              className={isEdit ? "bi bi-pencil-square" : "bi bi-trash3-fill"}
            ></i>
          </div>
        </div>
      </HasRole>
    </Fragment>
  );
};

export default InventoryAddDeleteButton;
